/**
 * @file element-font-lines.ts
 * @description Строки подсказки «Аа Шрифт заголовков · Montserrat» в панели
 * элемента, каталоге и подвале (phase19.16, приёмка Р2а, §7.17 п. 3; SB10,
 * дополнение 2026-10-11; проект uiux-fonts-usage §4, §6).
 *
 * Чистые функции без React: части элемента и их роли дают
 * `elementFontParts` / `*_FONT_PARTS` из shared, здесь — какой шрифт реально
 * стоит у каждой части (`effectiveFontKey`), его название из реестра и
 * состояния §6: акцент не выбран, «ваш», без русских букв, снят из каталога,
 * список своих шрифтов ещё не получен или не получился.
 */

import {
  EYEBROW_TITLE,
  FONT_ROLES,
  effectiveFontKey,
  isShopFontEntry,
  isShopFontKey,
  nearestFontWeight,
  resolveFontRole,
  type FontPart,
  type FontPartRole,
  type FontRole,
  type FontRoleTheme,
  type SiteFontRegistry,
  type SiteTheme,
} from "@chaiberi/shared";

import { WEIGHT_LABELS } from "../site-view/font-weights";

/** Что известно о списке своих шрифтов магазина (`useShopFonts`). */
export interface FontListState {
  registry: SiteFontRegistry;
  /** Список получен с сервера. */
  loaded: boolean;
  /** Список не получился. */
  error: boolean;
  /** Список вообще запрашивается (есть право на оформление). */
  requested: boolean;
}

/** Строка подсказки: часть элемента и шрифт, который у неё стоит. */
export interface FontNoteLine {
  /** Подпись части; `""` — у элемента одна роль. */
  part: string;
  role: FontPartRole;
  /** Ключ шрифта, который реально стоит на сайте. */
  key: string;
  /** Роль, чей шрифт применён (у невыбранного акцента — заголовков). */
  appliedRole: FontRole;
  /** Акцент не выбран — стоит шрифт заголовков. */
  fallback: boolean;
  /** Название шрифта; `null` — неизвестно (свой шрифт без списка). */
  family: string | null;
  /** Свой шрифт магазина. */
  own: boolean;
  /** Свой шрифт без русских букв. */
  noCyrillic: boolean;
  /** Шрифт снят из каталога платформы. */
  retired: boolean;
  /** Название ещё грузится (список своих шрифтов в пути). */
  pending: boolean;
}

/** Строки подсказки по частям элемента. */
export function fontNoteLines(parts: readonly FontPart[], theme: FontRoleTheme, fonts: FontListState): FontNoteLine[] {
  return parts.map(({ part, role }) => {
    const applied = effectiveFontKey(role, theme);
    const entry = fonts.registry.find(applied.key);
    const ownKey = isShopFontKey(applied.key);
    const shop = entry !== undefined && isShopFontEntry(entry) ? entry : null;
    // Свой шрифт не найден: список в пути — заготовка, не получился — без названия, иначе «свой шрифт».
    const pending = !entry && ownKey && fonts.requested && !fonts.loaded && !fonts.error;
    const unknownName = !entry && ownKey ? (fonts.error || pending ? null : "свой шрифт") : null;
    return {
      part,
      role,
      key: applied.key,
      appliedRole: applied.role,
      fallback: applied.fallback,
      family: entry?.family ?? unknownName,
      own: ownKey,
      noCyrillic: shop !== null && !shop.hasCyrillic,
      retired: entry !== undefined && !isShopFontEntry(entry) && entry.retired === true,
      pending,
    };
  });
}

/** Подпись строки до названия шрифта: «Шрифт заголовков», «Название — заголовков». */
export function fontLineLabel(line: FontNoteLine, theme: FontRoleTheme): string {
  if (line.fallback) {
    return line.part
      ? `${line.part} — акцент не выбран, сейчас шрифт заголовков`
      : "Шрифт акцента не выбран — сейчас шрифт заголовков";
  }
  const genitive = FONT_ROLES[resolveFontRole(line.role, theme)].genitive;
  if (line.part) return `${line.part} — ${genitive}`;
  return line.role === "eyebrow" ? `${EYEBROW_TITLE} — ${genitive}` : `Шрифт ${genitive}`;
}

/** Название с пометками: «Lora (ваш)», «Lobster (больше не предлагается)»; `null` — без названия. */
export function fontLineName(line: FontNoteLine): string | null {
  if (line.family === null) return null;
  const marks = [line.own && line.family !== "свой шрифт" ? "ваш" : null, line.retired ? "больше не предлагается" : null].filter(Boolean);
  return marks.length > 0 ? `${line.family} (${marks.join(", ")})` : line.family;
}

/** Начертание и регистр заголовков: «жирное, как написано» — только у строки шрифта заголовков. */
export function headingStyleNote(line: FontNoteLine, theme: SiteTheme, registry: SiteFontRegistry): string | null {
  if (line.role !== "heading") return null;
  const weight = nearestFontWeight(line.key, theme.type.headingWeight, registry);
  const weightLabel = (WEIGHT_LABELS[weight] ?? String(weight)).toLowerCase();
  return `${weightLabel}, ${theme.type.headingCase === "upper" ? "прописные" : "как написано"}`;
}

/** Вес образца «Аа»: у заголовков — начертание из оформления, у остальных — обычное. */
export function sampleWeight(line: FontNoteLine, theme: SiteTheme): number {
  return line.role === "heading" ? theme.type.headingWeight : 400;
}
